import { useMemo } from 'react';

import { useAppStore, type IAppStore } from '@/store';
import useLibrarySectionsQuery from '@/hooks/queries/useLibrarySectionsQuery';
import useNewPresetsQuery from '@/hooks/queries/useNewPresetsQuery';
import { NEW_TAB_SLUG } from '@/store/homeTabs/types';

import type { LibraryItem } from '@/types';

const selector = (store: IAppStore) => ({
  activeTabSlug: store.activeTabSlug,
  entrySlug: store.presetFeedEntrySlug,
});

const usePresetFeedData = (): {
  items: LibraryItem[];
  initialIndex: number;
  isLoading: boolean;
} => {
  const { activeTabSlug, entrySlug } = useAppStore(selector);
  const { data: sections, isPending } = useLibrarySectionsQuery();

  // Cards in the "New" tab live in a different cache than the editorial rows,
  // and `activeTabSlug` is the only clue which one the feed was opened from.
  const isNewTab = activeTabSlug === NEW_TAB_SLUG;
  const { data: newSection, isPending: isNewPending } =
    useNewPresetsQuery(isNewTab);

  return useMemo(() => {
    const items = isNewTab
      ? newSection?.items ?? []
      : sections?.find((section) => section.slug === activeTabSlug)?.items ??
        [];
    const foundIndex = items.findIndex((item) => item.slug === entrySlug);

    return {
      items,
      initialIndex: foundIndex >= 0 ? foundIndex : 0,
      isLoading:
        Boolean(activeTabSlug) && (isNewTab ? isNewPending : isPending),
    };
  }, [
    sections,
    newSection,
    isNewTab,
    entrySlug,
    activeTabSlug,
    isPending,
    isNewPending,
  ]);
};

export default usePresetFeedData;
